import { NextSeo } from 'next-seo';
import getConfig from 'next/config';
import { useRouter } from 'next/router';
import { GetServerSideProps, NextPage } from 'next';
import { useTranslations } from 'next-intl';
import { injectLayoutData } from '@utils/layout';
import { PrismicDocument } from '@prismicio/client';
import { prismicService, searchClient } from '@services/businessContainer';
import {
  formatPrismicData,
  getChannelFromLocale,
} from '@monofront/business/utils';
import SharedWishlist from '@screens/SharedWishlist';
import { IPrismicImage } from '@monofront/types';
import { injectGetServerSidePropsHeaders } from '@utils/query';
import {
  InstantSearch,
  InstantSearchSSRProvider,
} from 'react-instantsearch-core';
import {
  InstantSearchSSRProps,
  renderInstantSearchSSR,
} from '@utils/renderInstantSearchSSR';
import { InMemoryCache, ApolloClient } from '@apollo/client';
import { GET_WISHLIST_DETAILS } from '@monofront/business/queries';
import { getLegoUrl } from '@utils/config';

const {
  publicRuntimeConfig: { appBaseUrl },
} = getConfig();

type PageSharedWishlistsProps = InstantSearchSSRProps & {
  pageContent: ISharedWishlistPageContent;
  title: string | null;
  owner: string | null;
  skus: Array<string>;
};

export interface ISharedWishlistPageContent {
  addToCartButtonLabel: string;
  shareMyListBannerImage: IPrismicImage;
}

const NextSeoWishlist = () => {
  const router = useRouter();
  const { id } = router.query;

  const tWishlists = useTranslations('Wishlists');

  return (
    <NextSeo
      title={tWishlists('myLists')}
      canonical={`${appBaseUrl}/shared-list/${id}`}
      noindex={true}
      nofollow={false}
      openGraph={{
        url: `${appBaseUrl}/shared-list/${id}`,
        title: tWishlists('myLists'),
      }}
    />
  );
};

const PageWishlist: NextPage<PageSharedWishlistsProps> = ({
  pageContent,
  baseIndexName,
  searchServerState,
  title,
  owner,
  skus,
}) => {
  return (
    <InstantSearchSSRProvider {...searchServerState}>
      <InstantSearch
        indexName={baseIndexName}
        searchClient={searchClient}
        insights
        future={{
          preserveSharedStateOnUnmount: true,
        }}
      >
        {searchServerState && <NextSeoWishlist />}
        <SharedWishlist
          pageContent={pageContent}
          owner={owner}
          title={title}
          skus={skus}
        />
      </InstantSearch>
    </InstantSearchSSRProvider>
  );
};

export const getServerSideProps =
  injectGetServerSidePropsHeaders<PageSharedWishlistsProps>(
    injectLayoutData<
      PageSharedWishlistsProps,
      GetServerSideProps<PageSharedWishlistsProps>
    >(async ({ req, previewData, locale, query }) => {
      if (!locale) {
        return {
          notFound: true,
        };
      }

      if (typeof query.id !== 'string' || !query.id) {
        return {
          notFound: true,
        };
      }

      const apolloClient = new ApolloClient({
        uri: `${getLegoUrl()}/graphql`,
        headers: {
          'lf-channel': getChannelFromLocale(locale),
        },
        cache: new InMemoryCache(),
      });

      let title: string | null = null;
      let owner: string | null = null;
      let skus: string[] = [];

      try {
        const { data } = await apolloClient.query({
          query: GET_WISHLIST_DETAILS,
          variables: {
            publicWishlistId: query.id,
          },
        });

        if (!data?.getWishlistDetails) {
          return {
            notFound: true,
          };
        }

        title = data.getWishlistDetails.title;
        owner = data.getWishlistDetails.ownerFirstName;
        skus = data.getWishlistDetails.skus;
      } catch {
        return {
          notFound: true,
        };
      }

      const results: PrismicDocument = await prismicService.getDocumentByUid(
        'shopwishlist',
        'mes-listes',
        previewData,
        {
          ...(locale ? { lang: `${locale}-${locale}` } : {}),
        },
      );
      const pageContent: ISharedWishlistPageContent = results?.data
        ? formatPrismicData(results.data)
        : null;

      const { baseIndexName, serverUrl, searchServerState } =
        await renderInstantSearchSSR({ req, locale }, PageWishlist, {
          pageContent,
          title,
          owner,
          skus,
        });

      return {
        props: {
          pageContent,
          title,
          owner,
          skus,
          searchServerState,
          baseIndexName,
          serverUrl,
        },
      };
    }),
  );

export default PageWishlist;
